import { useEffect, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { CityTypeahead } from '../../components/CityTypeahead'
import { ChangedElsewhereNotice } from '../../components/ChangedElsewhereNotice'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { FormCheckbox, FormField, FormSelect } from '../../components/FormField'
import { RecordPicker } from '../../components/RecordPicker'
import { describeBookRecord } from '../../components/bookRecord'
import { RecordLink } from '../../components/RecordLink'
import { apiErrorMessage } from '../../lib/errors'
import { CHANGED_ELSEWHERE } from '../../lib/formErrors'
import { formatBusinessDate, formatPhraseList } from '../../lib/format'
import { parseAmount } from '../../lib/number'
import { BroadcastsPanel } from '../broadcasts/BroadcastsPanel'
import {
  getCarrier,
  isOpenDeck,
  searchActiveCarriers,
  STRAIGHT_BOX_TRUCK,
  type CarrierDetail,
  type CarrierSummary,
} from '../carriers/api'
import { describeReason, unavailableBecause, type FleetStanding } from '../carriers/fleetStanding'
import {
  DEFAULT_DAYS,
  getAvailability,
  LOAD_TYPES,
  postAvailability,
  SECUREMENT_GEAR,
  updateAvailability,
  withdrawAvailability,
  type SecurementGear,
  type TruckAvailability,
  type TruckAvailabilityPayload,
} from './api'
import { messageFor, toPostingFormErrors, withoutPostingErrorsFor, type FormErrors } from './errors'
import { StandingBadge } from './StandingBadge'
import { unitEquipment } from './unitName'

const emptyForm = {
  truckId: '',
  trailerId: '',
  driverId: '',
  availableFrom: '',
  availableUntil: '',
  city: '',
  state: '',
  destinations: '',
  loadType: 'FTL',
  pallets: '',
  weight: '',
  reeferMin: '',
  reeferMax: '',
  securement: [] as SecurementGear[],
  instructions: '',
  minRate: '',
}

type PostingForm = typeof emptyForm

const noErrors: FormErrors = { fields: {}, summary: [] }

/** A posting's saved values, laid into the form's boxes. */
function formOf(posting: TruckAvailability): PostingForm {
  return {
    truckId: posting.truckId,
    trailerId: posting.trailerId ?? '',
    driverId: posting.driverId ?? '',
    availableFrom: posting.availableFrom,
    availableUntil: posting.availableUntil,
    city: posting.city,
    state: posting.state,
    destinations: posting.preferredDestinations.join(', '),
    loadType: posting.loadType,
    pallets: posting.availablePallets?.toString() ?? '',
    weight: posting.availableWeightPounds.toString(),
    reeferMin: posting.reeferMinFahrenheit?.toString() ?? '',
    reeferMax: posting.reeferMaxFahrenheit?.toString() ?? '',
    securement: posting.securement,
    instructions: posting.specialInstructions ?? '',
    minRate: posting.minRatePerMile?.toString() ?? '',
  }
}

/**
 * Posts a unit, or edits a posting. One component for `/availability/new` and
 * `/availability/:id`, the broker form's shape: posting lands on the posting's own page,
 * where the same form edits it and the posting can be withdrawn.
 *
 * <p>The units come from one carrier's family read, the same the carrier page shows, so
 * a unit that cannot be offered is listed — disabled, with the reason in its label —
 * rather than missing: "why can't I post my truck?" answers itself. The server still
 * judges every unit named; this list only explains.</p>
 */
export function AvailabilityFormPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id: postingId } = useParams()
  const isEditing = postingId !== undefined

  // The carrier a new posting is made from — searched for in its picker, never listed:
  // the book answers at most 500, and a carrier past them would not be choosable (A1).
  const [carrier, setCarrier] = useState<CarrierSummary | null>(null)
  const carrierId = carrier?.id ?? ''
  const [fleet, setFleet] = useState<CarrierDetail | null>(null)
  const [isLoadingFleet, setIsLoadingFleet] = useState(false)
  const [posting, setPosting] = useState<TruckAvailability | null>(null)
  const [form, setForm] = useState<PostingForm>(emptyForm)
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  // Saving, withdrawing and loading what is saved each answer with the whole posting, and
  // two answers in flight can land in either order — a save answered after a withdrawal
  // would show the posting open again. So the form runs one of them at a time (the load
  // form's finding, GPT-5.6 Sol on #32).
  const isBusy = isSubmitting || isWithdrawing || isLoadingSaved
  const [saved, setSaved] = useState(false)
  // The reading this form was opened on — set when the posting loads, by its own save
  // and by "load what is saved", never by anything else (see RecordVersion).
  const [version, setVersion] = useState<string | null>(null)

  // Opening the page: an empty form to post from, or the posting and its carrier.
  useEffect(() => {
    let active = true
    setIsLoading(true)
    setLoadFailure(null)

    void (async () => {
      try {
        if (postingId === undefined) {
          setCarrier(null)
          setFleet(null)
          setForm(emptyForm)
          setPosting(null)
          setVersion(null)
          return
        }

        const existing = await getAvailability(postingId)
        const family = await getCarrier(existing.carrierId)
        if (active) {
          setPosting(existing)
          setFleet(family)
          setForm(formOf(existing))
          setVersion(existing.updatedAt)
        }
      } catch (caught) {
        if (active) {
          setLoadFailure({ error: caught })
        }
      } finally {
        if (active) {
          setIsLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [postingId])

  // Choosing a carrier to post from brings its fleet; nothing typed is lost but the units.
  useEffect(() => {
    if (isEditing || carrierId === '') {
      if (!isEditing) {
        setFleet(null)
      }
      return
    }

    let active = true
    setIsLoadingFleet(true)

    void (async () => {
      try {
        const family = await getCarrier(carrierId)
        if (active) {
          setFleet(family)
        }
      } catch (caught) {
        if (active) {
          setLoadFailure({ error: caught })
        }
      } finally {
        if (active) {
          setIsLoadingFleet(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [carrierId, isEditing])

  function set<K extends keyof PostingForm>(field: K, value: PostingForm[K], errorField: string = field) {
    setForm((current) => ({ ...current, [field]: value }))
    setErrors((current) => withoutPostingErrorsFor(current, errorField))
    setSaved(false)
  }

  /**
   * A new carrier to post from. Its fleet replaces the old one's, and the old one goes at
   * once, not when the new one arrives: between the two, its units were still on the lists
   * and choosable, and a posting made from one of them belonged to the carrier that had
   * just left the screen (GPT-5.6 Sol on #28).
   */
  function chooseCarrier(next: CarrierSummary | null) {
    setCarrier(next)
    setFleet(null)
    setForm((current) => ({ ...current, truckId: '', trailerId: '', driverId: '' }))
    // All three boxes are emptied, so all three complaints go with them — a refusal about a
    // trailer from the previous carrier would otherwise sit under an empty picker (Copilot on #28).
    setErrors((current) =>
      ['truckId', 'trailerId', 'driverId'].reduce((errors, field) => withoutPostingErrorsFor(errors, field), current),
    )
  }

  /**
   * Picking a truck. A straight box truck carries its own load (TRKC-100): it is posted
   * without a trailer, so the trailer goes, and its own room is the posting's — re-answered
   * the way a trailer's is, never kept from the unit before. A box has no temperature to
   * hold and no deck to tie down. Any other truck still waits for its trailer.
   */
  function chooseTruck(truckId: string) {
    const truck = fleet?.trucks.find((row) => row.id === truckId)

    if (truck?.type !== STRAIGHT_BOX_TRUCK) {
      set('truckId', truckId)
      return
    }

    setForm((current) => ({
      ...current,
      truckId,
      trailerId: '',
      pallets: truck.palletCapacity?.toString() ?? '',
      weight: truck.weightCapacityPounds?.toString() ?? '',
      reeferMin: '',
      reeferMax: '',
      securement: [],
    }))
    setErrors((current) =>
      ['truckId', 'trailerId'].reduce((errors, field) => withoutPostingErrorsFor(errors, field), current),
    )
    setSaved(false)
  }

  /**
   * Picking a trailer brings what it states: its room becomes the posting's, and its
   * type decides which sections the form shows. The dispatcher lowers the room for a
   * partial afterwards; the server refuses anything above what the trailer states.
   */
  function chooseTrailer(trailerId: string) {
    const trailer = fleet?.trailers.find((row) => row.id === trailerId)
    // Picking a trailer always re-answers the room: what it states, or nothing at all.
    // Keeping the previous trailer's figures would hand the server numbers nobody typed
    // for this unit, and it has no way to tell them from deliberate ones (GPT-5.6 Sol).
    const stated = (capacity: number | null | undefined, typed: string) =>
      trailer === undefined ? typed : (capacity?.toString() ?? '')

    setForm((current) => ({
      ...current,
      trailerId,
      pallets: stated(trailer?.palletCapacity, current.pallets),
      weight: stated(trailer?.weightCapacityPounds, current.weight),
      reeferMin: trailer?.type === 'Reefer' ? current.reeferMin : '',
      reeferMax: trailer?.type === 'Reefer' ? current.reeferMax : '',
      securement: isOpenDeck(trailer?.type) ? current.securement : [],
    }))
    setErrors((current) => withoutPostingErrorsFor(current, 'trailerId'))
    setSaved(false)
  }

  /**
   * The units a picker lists: the ones we work with, and an inactive one only while the posting
   * names it, so an edit still shows what it holds (TRKC-110 item 2: inactive records are hidden
   * unless asked for). A unit held back for its paperwork stays, disabled with its reason —
   * paperwork comes back on its own; an inactive unit waits for somebody to reactivate it.
   */
  function offered<T extends { id: string; active: boolean }>(rows: readonly T[] | undefined, chosen: string): T[] {
    return (rows ?? []).filter((row) => row.active || row.id === chosen)
  }

  /** The sentence behind a unit that cannot be offered, from the carrier screens' own reasons. */
  function unitOption(id: string, label: string, record: FleetStanding) {
    if (fleet === null || record.availableForDispatch) {
      return { value: id, label }
    }

    const reasons = unavailableBecause(record, fleet)
      .filter((reason) => reason.key !== 'compliance.unavailable')
      .map((reason) => describeReason(reason, t))
      .join(' · ')

    return {
      value: id,
      label: t('availability.unitUnavailable', { unit: label, reasons: reasons || t('compliance.unavailable') }),
      disabled: true,
    }
  }

  async function loadWhatIsSaved() {
    if (postingId === undefined) {
      return
    }

    setIsLoadingSaved(true)

    try {
      const current = await getAvailability(postingId)
      setPosting(current)
      setForm(formOf(current))
      setVersion(current.updatedAt)
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toPostingFormErrors(caught))
    } finally {
      setIsLoadingSaved(false)
    }
  }

  async function withdraw() {
    if (postingId === undefined) {
      return
    }

    setIsWithdrawing(true)

    try {
      setPosting(await withdrawAvailability(postingId))
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toPostingFormErrors(caught))
    } finally {
      setIsWithdrawing(false)
    }
  }

  /**
   * A box left blank is a value nobody gave; a box holding something the platform cannot
   * read — "2..5", or a paste long enough to overflow a double — is a value that would be
   * dropped on the way out, and an optional one is dropped in silence: the server has
   * nothing left to complain about (GPT-5.6 Sol on #28). Every numeric box is asked, with
   * the code the server itself would answer for that box, so the sentence says what to
   * type either way.
   */
  function unreadableNumbers(): Record<string, string> {
    const unreadable: Record<string, string> = {}

    for (const [field, value, code] of [
      ['availablePallets', form.pallets, 'pallet_capacity_out_of_range'],
      ['availableWeight', form.weight, 'weight_out_of_range'],
      // The range is complained about on its lowest box, as the server's own code does.
      ['reeferMin', form.reeferMin, 'availability_reefer_range_invalid'],
      ['reeferMin', form.reeferMax, 'availability_reefer_range_invalid'],
      ['minRatePerMile', form.minRate, 'availability_min_rate_invalid'],
    ] as const) {
      if (value.trim() !== '' && parseAmount(value) === null) {
        unreadable[field] = code
      }
    }

    return unreadable
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors(noErrors)
    setSaved(false)

    const unreadable = unreadableNumbers()

    if (Object.keys(unreadable).length > 0) {
      setErrors({ fields: unreadable, summary: [] })
      return
    }

    const payload: TruckAvailabilityPayload = {
      truckId: blankToNull(form.truckId),
      // Choosing a straight box truck let the trailer go (chooseTruck): the box is empty.
      trailerId: blankToNull(form.trailerId),
      driverId: blankToNull(form.driverId),
      availableFrom: blankToNull(form.availableFrom),
      availableUntil: blankToNull(form.availableUntil),
      city: form.city.trim(),
      state: form.state.trim(),
      preferredDestinations: form.destinations
        .split(/[\s,;]+/)
        .map((entry) => entry.trim())
        .filter((entry) => entry !== ''),
      loadType: form.loadType as TruckAvailabilityPayload['loadType'],
      availablePallets: parseAmount(form.pallets),
      availableWeightPounds: parseAmount(form.weight),
      reeferMinFahrenheit: parseAmount(form.reeferMin),
      reeferMaxFahrenheit: parseAmount(form.reeferMax),
      securement: form.securement,
      specialInstructions: blankToNull(form.instructions),
      minRatePerMile: parseAmount(form.minRate),
      version: version ?? undefined,
    }

    setIsSubmitting(true)

    try {
      if (postingId === undefined) {
        const created = await postAvailability(payload)
        void navigate(`/availability/${created.id}`, { replace: true })
        return
      }

      const updated = await updateAvailability(postingId, payload)
      setPosting(updated)
      setForm(formOf(updated))
      setVersion(updated.updatedAt)
      setSaved(true)
    } catch (caught) {
      setErrors(toPostingFormErrors(caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <p className="session-check">{t('availability.loading')}</p>
  }

  if (loadFailure !== null) {
    return (
      <section className="page">
        <Breadcrumbs
          trail={[
            { label: t('nav.availability'), to: '/availability' },
            { label: isEditing ? t('availability.editTitle') : t('availability.postTitle') },
          ]}
        />
        <header className="page__header">
          <h1>{isEditing ? t('availability.editTitle') : t('availability.postTitle')}</h1>
        </header>
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'availability.loadError')}
        </p>
        <p>
          <Link to="/availability">{t('availability.backToBoard')}</Link>
        </p>
      </section>
    )
  }

  const field = (name: string): ReactNode => {
    const code = errors.fields[name]

    if (code === undefined) {
      return undefined
    }

    const subject = errors.subjects?.[code]
    const sentence = messageFor(code, t, 'availability.saveError', subject)

    return subject === undefined ? (
      sentence
    ) : (
      <>
        {sentence} <RecordLink subject={subject} />
      </>
    )
  }

  const fieldText = (name: string): string | undefined => {
    const code = errors.fields[name]
    return code === undefined ? undefined : messageFor(code, t, 'availability.saveError', errors.subjects?.[code])
  }

  const trailer = fleet?.trailers.find((row) => row.id === form.trailerId)
  const truck = fleet?.trucks.find((row) => row.id === form.truckId)
  // A straight box truck is posted alone (TRKC-100). An edit reads the posting and its
  // carrier's fleet together, so the truck is always there to ask.
  const isBox = truck?.type === STRAIGHT_BOX_TRUCK
  // What carries the load decides the sections: the box truck itself, or the trailer.
  const haulerType = isBox ? STRAIGHT_BOX_TRUCK : trailer?.type
  const withdrawn = posting?.standing === 'Withdrawn'
  const truckLabel = posting === null ? null : posting.truckUnitNumber

  return (
    <section className="page">
      <Breadcrumbs
        trail={[
          { label: t('nav.availability'), to: '/availability' },
          { label: isEditing ? (posting?.reference ?? null) : t('breadcrumbs.newPosting') },
        ]}
      />
      <header className="page__header page__header--row">
        <div>
          <h1>{isEditing ? t('availability.editTitle') : t('availability.postTitle')}</h1>
          {posting !== null && (
            <p className="page__subtitle">
              <StandingBadge standing={posting.standing} /> {posting.reference} · {posting.carrierName}
            </p>
          )}
        </div>
        {posting !== null && posting.standing === 'Open' && (
          <div className="page__actions">
            <Link className="button button--quiet" to={`/availability/${posting.id}/broadcast`}>
              {t('broadcasts.start')}
            </Link>
            <Link className="button" to={`/availability/${posting.id}/matches`}>
              {t('matching.openLoads')}
            </Link>
          </div>
        )}
      </header>

      {saved && (
        <p className="page__subtitle" role="status">
          {t('availability.saved')}
        </p>
      )}

      {/* An open posting whose unit lost its standing after it was posted: shown, never
          withdrawn behind the dispatcher's back, and named so it can be fixed. */}
      {posting !== null && posting.standing === 'Open' && posting.heldBackBy.length > 0 && (
        <div className="alert alert--warning" role="status">
          {posting.heldBackBy.map((holdback) => (
            <p key={holdback.unit}>
              {t('availability.heldBackBy', {
                unit: t(`availability.units.${holdback.unit}`),
                name: holdback.culprit.name,
              })}{' '}
              <RecordLink subject={holdback.culprit} />
            </p>
          ))}
        </div>
      )}

      {withdrawn && (
        <p className="alert alert--warning" role="status">
          {t('availability.withdrawnNote', {
            // The day the withdrawal fell on in the business zone, not in UTC.
            when: posting?.withdrawnAt == null ? '' : formatBusinessDate(posting.withdrawnAt, i18n.language),
          })}
        </p>
      )}

      {errors.summary.includes(CHANGED_ELSEWHERE) ? (
        <ChangedElsewhereNotice
          onLoadSaved={() => void loadWhatIsSaved()}
          isLoading={isLoadingSaved}
          disabled={isSubmitting || isWithdrawing}
        />
      ) : (
        errors.summary.length > 0 && (
          <div className="alert" role="alert">
            {errors.summary.map((code) => (
              <p key={code}>{messageFor(code, t, 'availability.saveError')}</p>
            ))}
          </div>
        )
      )}

      <form className="form form--stacked" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="fieldset" disabled={withdrawn}>
          <legend>{t('availability.unitSection')}</legend>

          {isEditing ? (
            <p className="field">
              <span>{t('availability.truck')}</span>
              <strong>{truckLabel}</strong>
            </p>
          ) : (
            <>
              {/* Only the carriers we work with: a unit is posted from one in play. */}
              <RecordPicker
                name="carrierId"
                label={t('availability.carrier')}
                value={carrier}
                onChange={chooseCarrier}
                search={searchActiveCarriers}
                describe={(record) => describeBookRecord(record, t)}
                placeholder={t('picker.placeholder')}
                required
              />
              <FormSelect
                name="truckId"
                label={t('availability.truck')}
                value={form.truckId}
                onChange={chooseTruck}
                options={[
                  { value: '', label: t('availability.chooseTruck') },
                  ...offered(fleet?.trucks, form.truckId).map((row) =>
                    unitOption(
                      row.id,
                      // A box truck says so in the list: it is the one posted without a trailer.
                      row.type === STRAIGHT_BOX_TRUCK
                        ? `${row.unitNumber} · ${t('carriers.truckTypes.StraightBoxTruck')}`
                        : row.unitNumber,
                      row,
                    ),
                  ),
                ]}
                error={field('truckId')}
                required
                disabled={isLoadingFleet}
              />
            </>
          )}

          {isBox ? (
            <p className="field">
              <span>{t('availability.trailer')}</span>
              <strong>{t('availability.noTrailerForBoxTruck')}</strong>
            </p>
          ) : (
            <FormSelect
              name="trailerId"
              label={t('availability.trailer')}
              value={form.trailerId}
              onChange={chooseTrailer}
              options={[
                { value: '', label: t('availability.chooseTrailer') },
                ...offered(fleet?.trailers, form.trailerId).map((row) =>
                  unitOption(row.id, `${row.trailerNumber} · ${t(`carriers.types.${row.type}`)}`, row),
                ),
              ]}
              error={field('trailerId')}
              required
              disabled={isLoadingFleet}
            />
          )}
          <FormSelect
            name="driverId"
            label={t('availability.driver')}
            value={form.driverId}
            onChange={(value) => set('driverId', value)}
            options={[
              { value: '', label: t('availability.noDriver') },
              ...offered(fleet?.drivers, form.driverId).map((driver) =>
                unitOption(driver.id, `${driver.firstName} ${driver.lastName}`, driver),
              ),
            ]}
            error={field('driverId')}
            disabled={isLoadingFleet}
          />
          {isBox && truck !== undefined && (
            <p className="field">
              <span>{t('availability.equipment')}</span>
              <strong>{t('availability.equipmentFromTruck', {
                equipment: unitEquipment({ equipment: STRAIGHT_BOX_TRUCK, boxLengthFeet: truck.boxLengthFeet }, t),
                truck: truck.unitNumber,
              })}</strong>
              {/* A box truck's own features go with the posting, as a trailer's do (TRKC-100). */}
              {truck.features.length > 0 && (
                <span className="muted">
                  {formatPhraseList(
                    truck.features.map((feature) => t(`equipment.features.${feature}`)),
                    i18n.language,
                    true,
                  )}
                </span>
              )}
            </p>
          )}
          {!isBox && trailer !== undefined && (
            <p className="field">
              <span>{t('availability.equipment')}</span>
              <strong>{t('availability.equipmentFromTrailer', {
                equipment: t(`carriers.types.${trailer.type}`),
                trailer: trailer.trailerNumber,
              })}</strong>
              {/* What the trailer states it has goes with the posting, to matching and to the
                  brokers' email; it is the trailer's to change, never the posting's (TRKC-100). */}
              {trailer.features.length > 0 && (
                <span className="muted">
                  {formatPhraseList(
                    trailer.features.map((feature) => t(`equipment.features.${feature}`)),
                    i18n.language,
                    true,
                  )}
                </span>
              )}
            </p>
          )}
        </fieldset>

        <fieldset className="fieldset" disabled={withdrawn}>
          <legend>{t('availability.whenAndWhere')}</legend>

          <FormField
            name="availableFrom"
            label={t('availability.availableFrom')}
            type="date"
            value={form.availableFrom}
            onChange={(value) => set('availableFrom', value)}
            error={field('availableFrom')}
            required
          />
          <FormField
            name="availableUntil"
            label={t('availability.availableUntil')}
            type="date"
            value={form.availableUntil}
            onChange={(value) => set('availableUntil', value)}
            error={field('availableUntil')}
            placeholder={t('availability.untilHint', { days: DEFAULT_DAYS })}
          />
          <CityTypeahead
            name="city"
            label={t('availability.city')}
            value={form.city}
            onChange={(value) => set('city', value)}
            onSelect={({ city, state }) => {
              set('city', city)
              set('state', state)
            }}
            error={fieldText('city')}
            maxLength={100}
            required
            preferredState={form.state}
          />
          <FormField
            name="state"
            label={t('availability.state')}
            value={form.state}
            onChange={(value) => set('state', value.toUpperCase())}
            error={field('state')}
            maxLength={2}
            placeholder="IL"
            required
            width="narrow"
          />
          <FormField
            name="destinations"
            label={t('availability.preferredDestinations')}
            value={form.destinations}
            onChange={(value) => set('destinations', value.toUpperCase(), 'preferredDestinations')}
            error={field('preferredDestinations')}
            placeholder="TX, OK, AR"
          />
        </fieldset>

        <fieldset className="fieldset" disabled={withdrawn}>
          <legend>{t('availability.roomSection')}</legend>

          <FormSelect
            name="loadType"
            label={t('availability.loadType')}
            value={form.loadType}
            onChange={(value) => set('loadType', value)}
            options={LOAD_TYPES.map((type) => ({ value: type, label: t(`availability.loadTypes.${type}`) }))}
            error={field('loadType')}
            required
          />
          <FormField
            name="availablePallets"
            label={t('availability.availablePallets')}
            value={form.pallets}
            onChange={(value) => set('pallets', value.replace(/\D/g, ''), 'availablePallets')}
            error={field('availablePallets')}
            inputMode="numeric"
            width="narrow"
          />
          <FormField
            name="availableWeight"
            label={t('availability.availableWeight')}
            value={form.weight}
            onChange={(value) => set('weight', value.replace(/[^\d.]/g, ''), 'availableWeight')}
            error={field('availableWeight')}
            inputMode="decimal"
            required
            width="narrow"
          />

          {/* A reefer always says what it can hold (TRKC-110); the server refuses one that does not. */}
          {haulerType === 'Reefer' && (
            <>
              <FormField
                name="reeferMin"
                label={t('availability.reeferMin')}
                value={form.reeferMin}
                onChange={(value) => set('reeferMin', value.replace(/[^\d-]/g, ''))}
                error={field('reeferMin')}
                inputMode="numeric"
                required
                width="narrow"
              />
              <FormField
                name="reeferMax"
                label={t('availability.reeferMax')}
                value={form.reeferMax}
                onChange={(value) => set('reeferMax', value.replace(/[^\d-]/g, ''), 'reeferMax')}
                inputMode="numeric"
                required
                width="narrow"
              />
            </>
          )}

          {/* An open deck — a flatbed, a step deck or a double drop — ties its freight down (TRKC-100). */}
          {isOpenDeck(haulerType) && (
            <div className="field field--choices" role="group" aria-labelledby="securement-label">
              <span id="securement-label">{t('availability.securement')}</span>
              {SECUREMENT_GEAR.map((gear) => (
                <FormCheckbox
                  key={gear}
                  name={`securement-${gear}`}
                  label={t(`availability.gear.${gear}`)}
                  checked={form.securement.includes(gear)}
                  onChange={(checked) =>
                    set(
                      'securement',
                      checked
                        ? [...form.securement, gear]
                        : form.securement.filter((existing) => existing !== gear),
                    )
                  }
                />
              ))}
              {field('securement') !== undefined && (
                <span className="field__error" role="alert">
                  {field('securement')}
                </span>
              )}
            </div>
          )}

          <FormField
            name="minRatePerMile"
            label={t('availability.minRatePerMile')}
            value={form.minRate}
            onChange={(value) => set('minRate', value.replace(/[^\d.]/g, ''), 'minRatePerMile')}
            error={field('minRatePerMile')}
            inputMode="decimal"
            placeholder="2.10"
            width="narrow"
          />
          <FormField
            name="specialInstructions"
            label={t('availability.specialInstructions')}
            value={form.instructions}
            onChange={(value) => set('instructions', value, 'specialInstructions')}
            error={field('specialInstructions')}
            maxLength={500}
            width="wide"
          />
        </fieldset>

        <div className="form__actions">
          <Link className="button button--quiet" to="/availability">
            {t('availability.backToBoard')}
          </Link>
          {posting !== null && !withdrawn && (
            <button
              type="button"
              className="button button--quiet"
              onClick={() => void withdraw()}
              disabled={isBusy}
            >
              {t('availability.withdraw')}
            </button>
          )}
          {!withdrawn && (
            <button type="submit" className="button" disabled={isBusy}>
              {isSubmitting ? t('availability.saving') : isEditing ? t('availability.save') : t('availability.post')}
            </button>
          )}
        </div>
      </form>

      {posting !== null && <BroadcastsPanel postingId={posting.id} />}
    </section>
  )
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
